import { useEffect, useState } from 'react';
import PlayerStats, { STATS_VIEWS } from './PlayerStats';
import SessionHistoryView from '../Session/SessionHistoryView';
import ClassicMatchCard from '../Session/ClassicMatchCard';
import CardHeader from '../CardHeader/CardHeader';
import { isRankedSession, isRotationSession } from '../../utils/matchStats';

// MVPs only exist in rotation sessions, so classic-only tables hide the MVP column
const hasRankedRotation = (sessions) =>
  sessions.some((s) => isRankedSession(s) && isRotationSession(s));

const PlayerStatsTabs = ({
  displayedSession,
  overallStats,
  allPlayers,
  currentArchivedSessionIndex,
  archivedSessions,
  handlePrevSession,
  handleNextSession,
  handleRecalculateOverallStats,
  isRecalculating,
  onDeleteSession,
  canEditArchivedSession,
  isSessionActive,
  currentUser,
}) => {
  // Events moved to their own page (#50); Stats is MATCHES / OVERALL
  const determineInitialTab = () => {
    if (!isSessionActive && archivedSessions.length > 0) {
      return 'session';
    }
    return 'overall';
  };

  const [activeTab, setActiveTabInternal] = useState(determineInitialTab());
  const [hasUserSelectedTab, setHasUserSelectedTab] = useState(false);
  const [overallView, setOverallView] = useState('matches');

  const handleTabChange = (tab) => {
    setActiveTabInternal(tab);
    setHasUserSelectedTab(true);
  };

  const totalArchived = archivedSessions.length;

  let sessionNavTitle = 'No Past Sessions';
  let sessionDateDisplay = '';

  if (displayedSession.date === 'Current Session') {
    sessionNavTitle = 'Current Session';
  } else if (displayedSession.date !== 'No Past Sessions' && totalArchived > 0) {
    const currentIndex = currentArchivedSessionIndex === null ? 0 : currentArchivedSessionIndex;
    const displayIndex = totalArchived - currentIndex;
    sessionNavTitle = `Session History (${displayIndex}/${totalArchived})`;
    sessionDateDisplay = displayedSession.date;
  }

  useEffect(() => {
    const initial = determineInitialTab();

    if (!hasUserSelectedTab) {
      setActiveTabInternal(initial);
      return;
    }

    if (activeTab === 'session' && (isSessionActive || archivedSessions.length === 0)) {
      setActiveTabInternal(initial);
    }
  }, [isSessionActive, archivedSessions.length, activeTab, hasUserSelectedTab]);

  const showOverallMvp = hasRankedRotation(archivedSessions);
  // No MVP option when the club has only played classic matches
  const overallViews = STATS_VIEWS.filter((v) => v.value !== 'mvp' || showOverallMvp);
  const effectiveOverallView = overallViews.some((v) => v.value === overallView) ? overallView : 'matches';

  // Calculate tab count based on whether sessions tab is shown
  const showSessionsTab = !isSessionActive && archivedSessions.length > 0;
  const tabCount = showSessionsTab ? 2 : 1;

  const getIndicatorLeft = () => (showSessionsTab && activeTab === 'overall' ? '50%' : '0%');

  // Check if any sessions have been recorded
  const hasRecordedSessions = archivedSessions.length > 0;

  return (
    <div className="card mb-lg">
      {hasRecordedSessions && (
        <div className="tabs-container" style={{ '--tab-count': tabCount }}>
          <div className="tabs-group">
            {showSessionsTab && (
              <div 
                className={`tab ${activeTab === 'session' ? 'tab-selected' : 'tab-unselected'}`}
                onClick={() => handleTabChange('session')}
              >
                <div className="tab-contents">
                  <span className="tab-label">MATCHES</span>
                </div>
              </div>
            )}
            <div 
              className={`tab ${activeTab === 'overall' ? 'tab-selected' : 'tab-unselected'}`}
              onClick={() => handleTabChange('overall')}
            >
              <div className="tab-contents">
                <span className="tab-label">OVERALL</span>
              </div>
            </div>
            {/* Single sliding indicator */}
            <div 
              className="tab-indicator"
              style={{
                left: getIndicatorLeft(),
                width: `${100 / tabCount}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Tab Content Container */}
      <div style={{ position: 'relative', minHeight: '200px' }}>

        {/* Session Tab Content */}
        {showSessionsTab && (
          <div 
            key="session"
            className={`tab-content ${activeTab === 'session' ? 'tab-content-active' : 'tab-content-inactive'}`}
          >
            {activeTab === 'session' && !isSessionActive && (
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-4 mt-lg">
                  <button
                    onClick={handlePrevSession}
                    disabled={
                      displayedSession.date !== 'Current Session' &&
                      currentArchivedSessionIndex === totalArchived - 1
                    }
                    className="text-gray-500 dark:text-gray-400 disabled:opacity-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <h3 className="headline-small text-center flex-grow">
                    {sessionDateDisplay}
                  </h3>
                  <button
                    onClick={handleNextSession}
                    disabled={currentArchivedSessionIndex === null || displayedSession.date === 'Current Session'}
                    className="text-gray-500 dark:text-gray-400 disabled:opacity-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                {displayedSession.sessionMode === 'classic' || displayedSession.classicMatch || displayedSession.matchSummary?.classicMatch || displayedSession.sessionHistory?.[0]?.classicMatch ? (
                  <ClassicMatchCard
                    session={displayedSession}
                    allPlayers={allPlayers}
                    onDeleteSession={onDeleteSession}
                    isArchived={
                      displayedSession.date !== 'Current Session' && displayedSession.date !== 'No Past Sessions'
                    }
                    canEdit={canEditArchivedSession}
                  />
                ) : (
                  <SessionHistoryView
                    session={displayedSession}
                    allPlayers={allPlayers}
                    onDeleteSession={onDeleteSession}
                    isArchived={
                      displayedSession.date !== 'Current Session' && displayedSession.date !== 'No Past Sessions'
                    }
                    canEdit={canEditArchivedSession}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Overall Tab Content */}
        <div 
          key="overall"
          className={`tab-content ${activeTab === 'overall' ? 'tab-content-active' : 'tab-content-inactive'}`}
        >
          {activeTab === 'overall' && (
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-sm mb-md mt-lg">
                <h3 className="headline-small text-left">Overall Standings</h3>
                <div className="stats-view-select">
                  <span className="stats-view-select-label">
                    {overallViews.find((v) => v.value === effectiveOverallView)?.label}
                  </span>
                  <span className="material-symbols-outlined stats-view-select-icon" aria-hidden="true">
                    expand_more
                  </span>
                  <select
                    aria-label="Show stats"
                    value={effectiveOverallView}
                    onChange={(e) => setOverallView(e.target.value)}
                  >
                    {overallViews.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>
                {/* {handleRecalculateOverallStats && (
                  <button
                    onClick={handleRecalculateOverallStats}
                    disabled={isRecalculating}
                    className="btn btn-secondary"
                    title="Sync stats"
                  >
                    {isRecalculating ? (
                      <svg
                        className="animate-spin h-5 w-5 text-gray-800"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 text-gray-800"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                      </svg>
                    )}
                  </button>
                )} */}
              </div>
              <PlayerStats
                stats={overallStats}
                allPlayers={allPlayers}
                showMvp={showOverallMvp}
                currentUser={currentUser}
                view={effectiveOverallView}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsTabs;

